import React, { createContext, useContext, useRef } from "react";
import PaybitoCaptcha from "../Custom-component/PaybitoCaptcha";
import { checkMobileOtpCount, sendMobileOtp } from "../Services/OtpService";
import { showToast } from "../CoreDataService";

const OtpContext = createContext();

export const useOtp = () => {
  const context = useContext(OtpContext);
  if (!context) {
    throw new Error("useOtp must be used within an OtpProvider");
  }
  return context;
};

export const OtpProvider = ({ children }) => {
  const captchaRef = useRef(null);

  /**
   * Checks OTP count and verifies captcha if totalcount >= 1.
   * @param {Object} payload { phone, countryCode, userId, uuid }
   * @param {string} param API URL parameter (e.g. 'registration', 'login')
   * @returns {Promise<string>} Captcha token (empty string if not required)
   */
  const verifyCaptchaIfRequired = async (payload, param) => {
    const { phone, countryCode, userId, uuid } = payload;
    
    const countPayload = {
      phone: phone || "",
      countryCode: countryCode || "",
      userId: userId || localStorage.getItem("userId") || localStorage.getItem("user_id") || "",
      uuid: uuid || localStorage.getItem("uuid") || "",
    };

    // 1. Step 1: Check OTP send count
    const countData = await checkMobileOtpCount(countPayload, param);

    // Check for error in countData response
    if (countData?.error?.error_data !== 0) {
      window.dispatchEvent(new CustomEvent("show-error-dialog", { detail: countData?.error }));
      throw new Error(countData?.error?.error_msg || "Failed to check OTP count");
    }

    let captchaToken = "";

    // 2. Step 2: If totalcount >= 1, require captcha
    if (countData && countData.totalcount >= 1) {
      if (!captchaRef.current) {
        throw new Error("Captcha component not initialized");
      }
      
      showToast("info", "Please complete the captcha to proceed");
      const { token } = await captchaRef.current.verify();
      captchaToken = token;
    }

    return captchaToken;
  };

  /**
   * Unified function to send mobile OTP with count check and captcha logic.
   * @param {Object} payload Base payload (phone, countryCode, userId, uuid, etc.)
   * @param {string} param API URL parameter (e.g. 'registration', 'login', 'changephone')
   * @returns {Promise<Object>} API response data
   */
  const sendSmartMobileOtp = async (payload, param) => {
    try {
      // 1. Check count and verify captcha if needed
      const captchaToken = await verifyCaptchaIfRequired(payload, param);

      // 2. Send the OTP
      const { phone, countryCode, userId, uuid, ...rest } = payload;
      const sendPayload = {
        phone: phone || "",
        countryCode: countryCode || "",
        userId: userId || localStorage.getItem("userId") || localStorage.getItem("user_id") || "",
        uuid: uuid || localStorage.getItem("uuid") || "",
        ...rest, // Include password, socialMediaId, or any other metadata
        gRecaptchaResponse: captchaToken
      };

      const result = await sendMobileOtp(sendPayload, param);
      
      if (result?.error?.error_data === 0) {
        if (param === "changephone" || param === "changephonemobileotp") {
          showToast("success", "Input OTP has sent to your both registered & new phone no");
        } else {
          showToast("success", "Input OTP sent to your phone");
        }
      } else {
        window.dispatchEvent(new CustomEvent("show-error-dialog", { detail: result?.error }));
      }
      
      return result;
    } catch (error) {
      if (error.message === "captcha-cancelled") {
        console.log("Captcha cancelled by user");
      } else {
        console.error("OTP Flow Error:", error);
        showToast("error", error.message || "Something went wrong. Please try again.");
      }
      throw error;
    }
  };

  return (
    <OtpContext.Provider value={{ sendSmartMobileOtp, verifyCaptchaIfRequired }}>
      {children}
      <PaybitoCaptcha ref={captchaRef} />
    </OtpContext.Provider>
  );
};
